import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 2 — "La máquina 07" (Fábrica 13).
 * Mecánica distintiva: CONSIGNACIÓN (LOTO) antes de tocar la máquina.
 * Todo son datos; el motor es el mismo `WalkChapter`.
 */
export const FABRICA: ChapterData = {
  id: "fabrica",
  title: "Fábrica 13",
  location: "fabrica",
  secStart: 40,
  secGreat: 80,
  secOk: 66,
  secBad: 56,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "maquina", type: "machine", pos: { x: 7, y: 4 }, label: "Máquina 07", verb: "use", ref: "maquina", radius: 1.9, color: 0x4a545a },
    { id: "cuadro", type: "machine", pos: { x: 11, y: 4 }, label: "Cuadro eléctrico", verb: "use", ref: "cuadro", radius: 1.7, color: 0x8a5a1c },
    { id: "seta", type: "object", pos: { x: 5, y: 4 }, label: "Parada de emergencia", verb: "inspect", ref: "seta", radius: 1.3, color: 0xb23a2a },
    { id: "resguardo", type: "object", pos: { x: 9, y: 6 }, label: "Resguardo desmontado", verb: "inspect", ref: "resguardo", radius: 1.4, color: 0x7d868c },
    { id: "ruido", type: "object", pos: { x: 3, y: 3 }, label: "Zona de ruido", verb: "inspect", ref: "ruido", radius: 1.4, color: 0x1e2529 },
    { id: "trapo", type: "object", pos: { x: 7, y: 6 }, label: "Trapo en el rodillo", verb: "inspect", ref: "trapo", radius: 1.3, color: 0xc85a8a },
    { id: "orden", type: "document", pos: { x: 12.5, y: 7 }, label: "Orden de trabajo", verb: "read", ref: "orden", radius: 1.4 },
    { id: "bruno", type: "npc", pos: { x: 6, y: 7 }, label: "Bruno", verb: "talk", ref: "bruno", radius: 1.5, color: 0x2b3f6b },
    { id: "sara", type: "npc", pos: { x: 10, y: 7 }, label: "Sara", verb: "talk", ref: "sara", radius: 1.5, color: 0x1c6b64 },
    { id: "vega", type: "npc", pos: { x: 13, y: 8 }, label: "Vega", verb: "talk", ref: "vega", radius: 1.5, color: 0x2f2f36 },
    { id: "taquilla", type: "locker", pos: { x: 2, y: 8 }, label: "Taquilla de EPIs", verb: "open", radius: 1.6 },
    { id: "salida", type: "door", pos: { x: 12.5, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    maquina: {
      label: "Máquina 07",
      text: "La prensa <b>07</b>, en marcha. Le falta el <b>resguardo</b>: el punto de operación está al aire, a un palmo de las manos. Riesgo de <b>atrapamiento</b>.",
      note: { section: "pistas", heading: "Máquina 07 sin resguardo", body: "La prensa trabaja con el punto de operación abierto. Sin resguardo no hay barrera entre la mano y la parte móvil." },
      hazardId: "h_atrapamiento",
      comment: "Una máquina así, abierta y en marcha, es una trampa esperando un despiste.",
      action: {
        q: "La prensa 07 trabaja con el punto de operación al aire. ¿Qué haces?",
        options: [
          { text: "Parar y consignar la máquina; no reanudar sin el resguardo", ok: true, why: "Sin energía y con el resguardo puesto no hay atrapamiento posible. Así se trabaja." },
          { text: "Avisar a los operarios de que tengan cuidado", ok: false, why: "'Cuidado' no es una barrera física. Un despiste y la mano entra." },
          { text: "Trabajar rápido para exponerse poco tiempo", ok: false, why: "El riesgo no se mide en tiempo: basta un movimiento para el atrapamiento." },
        ],
        note: { section: "epis", heading: "Actuación: máquina consignada", body: "Prensa 07 parada y consignada; no se reanuda sin el resguardo." },
      },
    },
    cuadro: {
      label: "Cuadro eléctrico",
      text: "El <b>cuadro</b> de la 07. El enganche del candado está <b>vacío</b>: nadie ha consignado la energía antes de meter mano.",
      note: { section: "pistas", heading: "Sin consignación", body: "El cuadro no está bloqueado ni etiquetado. Cualquiera puede dar tensión con alguien trabajando dentro." },
      hazardId: "h_consig",
      comment: "Ni candado, ni etiqueta. Cualquiera le da al interruptor y la máquina arranca contigo dentro.",
      action: {
        q: "El cuadro de la 07 no está bloqueado. ¿Qué haces antes de que alguien meta mano?",
        options: [
          { text: "Consignar: cortar, bloquear con candado y etiquetar", ok: true, why: "Consignación (LOTO): nadie puede dar tensión con alguien dentro." },
          { text: "Pedir a un compañero que vigile el interruptor", ok: false, why: "Un vigilante no es una consignación. Si se distrae, la máquina arranca." },
          { text: "Poner una nota de 'no tocar'", ok: false, why: "Una nota se cae o se ignora. Hace falta bloqueo físico con candado." },
        ],
        note: { section: "epis", heading: "Actuación: energía consignada", body: "Cuadro de la 07 cortado, bloqueado con candado y etiquetado (LOTO)." },
      },
    },
    seta: {
      label: "Parada de emergencia",
      text: "La <b>seta de parada de emergencia</b>, tapada con una cruz de cinta aislante. Anulada a mano para que 'no pare la producción'.",
      note: { section: "pistas", heading: "Parada de emergencia anulada", body: "La seta de emergencia está tapada con cinta. Si algo va mal, no hay forma rápida de parar la máquina." },
      hazardId: "h_seta",
      comment: "Han tapado la seta de emergencia con cinta. Justo lo único que te salva cuando todo se tuerce.",
      signs: ["w_atrapamiento"],
      assess: {
        identify: {
          q: "¿Qué riesgo supone anular la parada de emergencia?",
          options: [
            { text: "No poder detener la máquina al instante ante un atrapamiento", ok: true },
            { text: "Riesgo eléctrico del propio pulsador", ok: false },
            { text: "Exceso de ruido de la seta", ok: false },
          ],
          hint: "La seta es la última barrera para pararlo todo cuando algo va mal. Piensa qué pasa si no responde en ese momento.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Quitar la cinta y verificar que la seta detiene la máquina", tier: "eliminar", grade: "best", why: "La parada de emergencia debe estar siempre operativa: restaurarla devuelve la última barrera de seguridad. Nunca se anula." },
            { text: "Poner un cartel de 'no anular la parada de emergencia'", tier: "organizativa", grade: "ok", why: "Avisar está bien, pero si la seta sigue con cinta no sirve de nada: primero hay que restaurarla." },
            { text: "Dejarla y trabajar con cuidado para no tener que pararla", tier: "ninguna", grade: "bad", why: "Confiar en no necesitar la emergencia es ignorar el riesgo: la medida menos eficaz.", danger: "Una mano entra en la prensa, alguien golpea la seta con el codo... y no pasa nada: la cinta la había anulado." },
          ],
        },
        apply: {
          intro: "Devuelve la parada de emergencia a servicio. Haz los pasos en orden.",
          steps: [
            "Detener la línea antes de tocar la seta",
            "Retirar la cinta que anula la seta",
            "Pulsar la seta y comprobar que la máquina se detiene",
            "Rearmar y dejar la seta libre y accesible",
          ],
          wrong: "Ese paso todavía no. Primero se para la línea; nunca se prueba una emergencia con la máquina en marcha sin control.",
          success: "Seta destapada y probada: vuelve a parar la máquina al instante.",
        },
        note: { section: "epis", heading: "La parada de emergencia no se anula", body: "La seta de emergencia debe estar siempre accesible y operativa. Restaurarla es prioritario; señalizar es complementario." },
      },
    },
    resguardo: {
      label: "Resguardo desmontado",
      text: "El <b>resguardo</b> de la 07, desmontado y apoyado en la pared, con polvo encima. Lleva tiempo fuera.",
      note: { section: "objetos", heading: "Resguardo retirado", body: "El resguardo de la máquina está desmontado desde hace tiempo. Alguien decidió que estorbaba." },
      comment: "El resguardo, criando polvo en la pared. En la máquina no protege a nadie.",
      signs: ["w_atrapamiento", "m_guantes"],
    },
    ruido: {
      label: "Zona de ruido",
      text: "Un sonómetro marca <b>92 dB(A)</b> junto a la línea. Muy por encima del nivel al que ya es obligatoria la protección auditiva... y nadie la lleva.",
      note: { section: "pistas", heading: "Ruido sobre el valor de acción", body: "92 dB(A) de forma continua. Por encima del valor superior de exposición (85 dB(A)) es obligatorio usar protección auditiva y señalizar la zona. Aquí no se usa." },
      hazardId: "h_ruido",
      comment: "Noventa y dos decibelios. Aquí se pierde oído sin enterarte, poco a poco.",
      signs: ["m_auditiva", "i_sonometro"],
      instrument: {
        device: "i_sonometro",
        button: "Medir el ruido",
        kind: "gauge",
        from: 46,
        to: 92,
        unit: "dB",
        limit: 85,
        danger: true,
        reading: "92 dB(A) de forma continua, muy por encima del valor superior de exposición (85 dB(A)): es obligatorio usar protección auditiva y señalizar la zona. (El valor límite, 87 dB(A), tampoco puede superarse.)",
        note: { section: "pistas", heading: "Medición: 92 dB(A)", body: "El sonómetro marca 92 dB(A) sostenidos. A partir de 85 dB(A) (valor superior de exposición) es obligatoria la protección auditiva y señalizar; 87 dB(A) es el valor límite que no debe superarse. 80 dB(A) es el valor inferior (facilitar protectores y formación)." },
      },
      assess: {
        identify: {
          q: "¿Qué riesgo hay en esta zona?",
          options: [
            { text: "Pérdida de audición por ruido continuo por encima del valor de acción", ok: true },
            { text: "Riesgo de atrapamiento", ok: false },
            { text: "Estrés térmico por calor", ok: false },
          ],
          hint: "El sonómetro marca 92 dB(A), muy por encima de 85 dB(A): a partir de ese nivel la protección es obligatoria. Piensa qué le pasa al oído expuesto día tras día.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Reducir el ruido en origen: mantenimiento de la máquina y encapsulado/aislamiento", tier: "colectiva", grade: "best", why: "Actuar sobre la fuente (mantenimiento, encapsular, aislar) baja el ruido para todos a la vez: va por delante de repartir tapones." },
            { text: "Dar protección auditiva y señalizar la zona de ruido", tier: "epi", grade: "ok", why: "Obligatorio por encima de 85 dB(A), sí, pero el EPI es el último escalón: no reduce el ruido, solo protege a quien lo lleva. Mejor atacar la fuente." },
            { text: "Subir la voz y seguir sin protección", tier: "ninguna", grade: "bad", why: "No reduce el ruido ni protege a nadie: la peor opción.", danger: "Tras meses así, la audiometría lo confirma: una sordera que ya no se recupera." },
          ],
        },
        apply: {
          intro: "Reduce el ruido en su origen. Haz los pasos en orden.",
          steps: [
            "Revisar y ajustar/engrasar la máquina que hace el ruido",
            "Instalar el encerramiento acústico (capó)",
            "Aislar la bancada con silentblocks",
            "Volver a medir con el sonómetro para confirmar",
          ],
          wrong: "Ese paso todavía no. Primero se actúa sobre la máquina y el aislamiento; medir es lo último para confirmar.",
          success: "Ruido reducido en origen y confirmado con el sonómetro: baja para toda la línea.",
        },
        note: { section: "epis", heading: "Ruido: reducir en origen antes que tapones", body: "Ante ruido por encima del valor de acción (85 dB(A)), primero se reduce en la fuente (mantenimiento, encapsulado); la protección auditiva es obligatoria pero es el último recurso." },
      },
    },
    trapo: {
      label: "Trapo en el rodillo",
      text: "Un <b>trapo</b> enganchado a un dedo del rodillo en movimiento. Así empiezan los atrapamientos: por algo suelto que se acerca demasiado.",
      note: { section: "objetos", heading: "Trapo junto a la parte móvil", body: "Un trapo casi atrapado por el rodillo. Podría haber sido una mano." },
      comment: "Ese trapo casi entra en el rodillo. Podría haber sido una mano.",
      signs: ["w_atrapamiento"],
    },
    orden: {
      label: "Orden de trabajo",
      text: "La <b>orden de trabajo</b> de mantenimiento de la 07, firmada con prisa. La casilla de <b>'consignación realizada'</b> está en blanco.",
      note: { section: "expedientes", heading: "Orden de trabajo sin consignar", body: "La orden pedía consignar antes de intervenir. La casilla quedó vacía y se firmó igual." },
      giveItem: "orden",
      comment: "La casilla de la consignación, en blanco. El papel también avisaba. Nadie lo leyó.",
    },
  },

  npcs: {
    bruno: {
      name: "Bruno",
      role: "Operario de la 07",
      portrait: "bruno",
      note: { section: "personas", heading: "Bruno", body: "Operario de la máquina 07. Retiró el resguardo para desatascar más rápido." },
      dialogue: [
        { speaker: "npc", text: "¿Vienes por la 07? Va fina, más que nunca. No sé a qué viene tanto lío." },
        { speaker: "epi", text: "Va fina porque le habéis quitado media seguridad, Bruno." },
        { choices: [ { text: "¿Por qué está fuera el resguardo?", go: 3 }, { text: "La seta de emergencia está con cinta.", go: 4 } ] },
        { speaker: "npc", text: "El resguardo lo quité yo. Para desatascar hay que meter la mano, y con el resguardo no llegas. Se pierde mucho tiempo si paras.", flag: "bruno_resguardo", note: { section: "pistas", heading: "Resguardo quitado para ir rápido", body: "Bruno retiró el resguardo para desatascar con la máquina en marcha y no perder producción." }, end: true },
        { speaker: "npc", text: "Ah, eso... la seta saltaba sola y paraba la línea. Alguien le puso cinta y andando. Un lío menos.", flag: "bruno_seta", note: { section: "pistas", heading: "Seta anulada por falsas paradas", body: "La parada de emergencia daba paradas 'que molestaban', así que la anularon con cinta." }, end: true },
      ],
    },
    sara: {
      name: "Sara",
      role: "Responsable de prevención",
      portrait: "sara",
      note: { section: "personas", heading: "Sara", body: "Prevención. Avisó por escrito de la 07. Le dijeron que 'no puede parar'." },
      dialogue: [
        { speaker: "npc", text: "Menos mal que vienes. Llevo semanas avisando por escrito de la 07 y mis correos se pierden en un cajón." },
        { speaker: "epi", text: "Enséñame ese procedimiento. ¿Qué debería hacerse antes de meter mano a la máquina?" },
        { choices: [ { text: "¿Cuál es el procedimiento correcto?", go: 3 }, { text: "¿Quién decide ignorar tus avisos?", go: 4 } ] },
        { speaker: "npc", text: "Consignar, siempre, antes de tocarla: cortar la energía, bloquear con candado, señalizar y verificar que no arranca. Cuatro pasos. Está escrito y firmado. Aquí se los saltan los cuatro.", flag: "sara_loto", note: { section: "expedientes", heading: "Procedimiento de consignación", body: "Existe procedimiento: cortar energía, bloquear con candado, señalizar y verificar antes de intervenir. No se aplicaba." }, end: true },
        { speaker: "npc", text: "Arriba. Me responden siempre lo mismo: 'la 07 no puede parar, hay que sacar producción'. Como si una mano valiera menos que un lote. Y consignar antes de tocarla —cortar, bloquear, señalizar, verificar— pasa a ser opcional. No lo es.", flag: "sara_loto", note: { section: "expedientes", heading: "Procedimiento de consignación", body: "Dirección prioriza la producción sobre la seguridad y no se consigna (cortar, bloquear, señalizar, verificar) antes de intervenir. Sara lo avisó por escrito." }, end: true },
      ],
    },
    vega: {
      name: "Vega",
      role: "¿?",
      portrait: "vega",
      note: { section: "personas", heading: "Vega", body: "Personaje misterioso. Habla de 'números'. El 07 no sería el primero." },
      dialogue: [
        { speaker: "npc", text: "Máquina cero siete. Curioso, ¿verdad? Tú también los estás encontrando." },
        { speaker: "epi", text: "¿Encontrar el qué? ¿Y usted quién es?" },
        { speaker: "npc", text: "Reviso lo que otros prefieren no mirar. Cada protección que desaparece en esta ciudad lleva un número. El siete no es el primero. Ni será el último.", flag: "vega_numeros", note: { section: "pistas", heading: "Los números de Vega", body: "Vega insinúa que las protecciones se retiran de forma organizada, cada una con un número." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_atrapamiento", category: "Atrapamiento", severity: 3, discoverable: true, requiredAction: "Reponer el resguardo con enclavamiento", relatedEquipment: ["guantes"] },
    { id: "h_consig", category: "Eléctrico / consignación", severity: 3, discoverable: true, requiredAction: "Consignar (LOTO) antes de intervenir" },
    { id: "h_seta", category: "Parada de emergencia", severity: 2, discoverable: true, requiredAction: "Restablecer la seta de emergencia" },
    { id: "h_ruido", category: "Ruido", severity: 2, discoverable: true, requiredAction: "Protección auditiva", relatedEquipment: ["auditiva"] },
  ],

  mission: {
    id: "cap2_maquina_07",
    title: "La máquina 07",
    location: "fabrica",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con la gente de la fábrica", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos de la línea", target: "3" },
      { id: "extra", type: "extra", label: "Consigna la máquina 07", hidden: true },
      { id: "equip", type: "equip", label: "Prepárate en la taquilla", hidden: true },
      { id: "report", type: "report", label: "Interviene la máquina con seguridad", hidden: true },
    ],
  },

  epi: {
    needed: ["gafas", "guantes", "auditiva", "botas"],
    optional: ["casco", "chaleco"],
    overkill: ["arnes", "mascarilla"],
  },

  extra: {
    triggerId: "cuadro",
    type: "lockout",
    signs: ["w_elec", "w_atrapamiento"],
    label: "Consignar la máquina 07",
    steps: [
      { id: "avisar", text: "Avisar y parar la máquina 07." },
      { id: "cortar", text: "Cortar la energía en el interruptor principal del cuadro." },
      { id: "bloquear", text: "Bloquear el interruptor con un candado (consignación)." },
      { id: "senalizar", text: "Colocar la etiqueta 'No conectar: trabajos en curso'." },
      { id: "verificar", text: "Verificar que la máquina no arranca (energía a cero)." },
    ],
    wrong: "Te has saltado un paso. Con ese orden, la máquina <b>podría arrancar</b> mientras trabajas. Empieza otra vez: avisar y cortar la energía, luego bloquear, señalizar y verificar.",
    success: "Máquina cortada, bloqueada, etiquetada y verificada. Ahora es una máquina muerta: puedes trabajar sin miedo a que arranque sola. <b>Eso</b> es consignar (LOTO).",
    note: { section: "expedientes", heading: "Consignación (LOTO) realizada", body: "Avisar y parar, cortar energía, bloquear con candado, señalizar y verificar energía cero. Solo entonces se interviene." },
  },

  consequence: {
    title: "Vuelves a la máquina 07",
    lines: {
      gafas: "Sin gafas, una viruta de metal salta hacia tu cara. Por poco.",
      guantes: "Sin guantes, el filo de una chapa te hace un corte al retirar la pieza.",
      auditiva: "Sin protección auditiva, el ruido continuo te deja los oídos pitando.",
      botas: "Sin botas, una pieza metálica cae al suelo y te roza el pie.",
    },
    great: "Con la máquina consignada y el equipo adecuado, reponer el resguardo y restablecer la seta es una intervención limpia y segura.",
    greatOver: "El arnés y la mascarilla aquí sobran: no hay altura ni polvo peligroso.",
    ok: "Sale, pero con algún corte o susto que, sabiendo lo que sabes, no tocaba.",
    bad: "Meterse en la 07 sin el equipo básico, justo después de investigar esto, es de traca.",
  },

  workId: "maquina",
  reward: "tarjeta03",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 03", body: "Vega la dejó al marcharse. El 07 no venía solo: hay una lista." },
    { section: "expedientes", heading: "Caso: La máquina 07", body: "Atrapamiento evitado por protecciones anuladas (resguardo, seta) y falta de consignación. Resuelto. Cabo suelto: la tarjeta 03 y Vega." },
  ],
  resolveText:
    `<p>Con la máquina <b>consignada</b> y el equipo puesto, repones el <b>resguardo</b> con enclavamiento y restableces la <b>parada de emergencia</b>. La 07 vuelve a ser una máquina, no una trampa.</p>
     <div class="epi-callout">Vega deja una tarjeta sobre la mesa antes de irse. Otro número: <b>03</b>.<br>“Dos protecciones quitadas a propósito. Y un número que se repite.”</div>`,
};
